import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Signup.css';
import SEO from '../components/SEO';
import { API_ENDPOINTS, BACKEND_URL } from '../config/api';
import { setCookieAuthVerified, clearExplicitLogout, isCookieAuthActive, isAuthenticated } from '../utils/auth';
import { isInAppBrowser, getInAppBrowserName, openInSystemBrowser } from '../utils/detectWebView';

const AUTH_API_URL = API_ENDPOINTS.AUTH.GOOGLE;

function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isWebView, setIsWebView] = useState(false);
  const [webViewAppName, setWebViewAppName] = useState('');

  // Detect in-app browser on mount
  useEffect(() => {
    if (isInAppBrowser()) {
      setIsWebView(true);
      setWebViewAppName(getInAppBrowserName());
    }
  }, []);

  useEffect(() => {
    // Check if user is already logged in (cookie auth or token auth)
    if (isAuthenticated()) {
      navigate('/', { replace: true });
      return;
    }

    // Read from query params (?success=true&token=<jwt>&userId=...&email=...&name=...)
    const params = new URLSearchParams(location.search);
    
    // Check if user came from Google Ads (gclid parameter indicates Google Ads click)
    const gclid = params.get('gclid');
    if (gclid) {
      localStorage.setItem('user_from_ads', 'true');
      localStorage.setItem('gclid', gclid);
    }
    
    // Check for errors
    const errorParam = params.get('error');
    if (errorParam) {
      setError('Google authentication failed: ' + errorParam);
      return;
    }

    // Check for successful OAuth redirect with user data in query params
    const success = params.get('success');
    const urlEmail = params.get('email');
    const urlUserId = params.get('userId');
    const urlToken = params.get('token');

    if (success === 'true' && urlEmail) {
      // Store JWT token if provided in URL
      if (urlToken) {
        localStorage.setItem('auth_token', urlToken);
        console.log('🔑 [Signup] JWT token stored from URL');
      }
      
      // Always record login timestamp so 7-day session window starts now
      localStorage.setItem('auth_login_time', Date.now().toString());
      
      // Store non-sensitive user data for UI display
      localStorage.setItem('userEmail', urlEmail);
      
      const urlName = params.get('name');
      const urlPicture = params.get('pictureUrl') || params.get('picture');
      
      if (urlName) {
        localStorage.setItem('userName', decodeURIComponent(urlName.replace(/\+/g, ' ')));
      }
      
      if (urlPicture) {
        localStorage.setItem('userPicture', decodeURIComponent(urlPicture));
      }
      
      if (urlUserId) {
        localStorage.setItem('userId', urlUserId);
      }
      
      // Mark cookie-based auth as active (backend set HttpOnly cookie during redirect)
      setCookieAuthVerified(true);
      clearExplicitLogout();
      
      // Mark as new user for conversion tracking
      localStorage.setItem('is_new_user', 'true');
      
      // Fire Google Ads conversion event for successful signup
      console.log("🔥 SIGNUP SUCCESS EVENT FIRED");
      
      window.dataLayer = window.dataLayer || [];
      
      setTimeout(() => {
        window.dataLayer.push({
          event: "signup_success"
        });
      }, 500);
      
      // Notify parent window if this was a popup OAuth flow
      if (window.opener) {
        window.opener.postMessage({
          type: 'oauth:success',
          payload: { userId: urlUserId, email: urlEmail, name: urlName }
        }, window.location.origin);
      }
      
      // Trigger events for other components to detect auth change
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new Event('authChanged'));
      
      // Clean sensitive data (token) from URL before navigating
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Redirect to homepage
      navigate('/', { replace: true });
      return;
    }

    // Check for OAuth callback (code + state) for direct code exchange
    const code = params.get('code');
    const state = params.get('state');

    if (code && state) {
      handleCallback(code, state);
    }
  }, [location, navigate]);

  async function handleCallback(code, state) {
    setLoading(true);
    setError('');

    try {
      // console.log('Starting OAuth callback with code:', code.substring(0, 20) + '...');
      
      // Option 1: Try calling the callback endpoint to exchange code for user data
      const callbackUrl = `${AUTH_API_URL}/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
      
      // console.log('Calling callback URL:', callbackUrl);
      
      const response = await fetch(callbackUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      // console.log('Callback response status:', response.status, response.statusText);
      // console.log('Callback response headers:', {
      //   contentType: response.headers.get('content-type'),
      //   location: response.headers.get('location')
      // });

      // Check if response is a redirect
      if (response.status === 302 || response.status === 301) {
      // console.log('Backend returned redirect instead of JSON');
        setError('Backend is redirecting. Please configure your AWS Lambda to return JSON with user data.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Callback error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      // console.log('Response content-type:', contentType);

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        const textResponse = await response.text();
        console.error('Failed to parse JSON. Response body:', textResponse);
        throw new Error('Backend did not return valid JSON. Got: ' + textResponse.substring(0, 100));
      }

      // console.log('Auth callback response - FULL DATA:', JSON.stringify(data, null, 2));
      // console.log('Auth callback response - DATA KEYS:', Object.keys(data));
      // console.log('Auth callback response - DATA TYPE:', typeof data);

      // Check if backend returned an error
      if (data.error || data.message) {
        console.error('Backend returned error:', data.error || data.message);
        throw new Error(data.error || data.message || 'Authentication failed');
      }
      
      // Check if backend returned redirectUrl (wrong endpoint response)
      if (data.redirectUrl) {
        console.error('WRONG RESPONSE: Got redirectUrl instead of user data.');
        throw new Error('Backend configuration error: callback endpoint returned OAuth init response instead of user data');
      }

      // Check if data is empty or missing required fields
      if (!data || Object.keys(data).length === 0) {
        throw new Error('Backend returned empty response');
      }

      if (!data.email && !data.userEmail) {
        console.error('WARNING: No email in response! Available fields:', Object.keys(data));
      }

      // Store JWT token if provided in response
      const token = data.token || data.jwt || data.accessToken;
      if (token) {
        localStorage.setItem('auth_token', token);
        console.log('🔑 [Signup] JWT token stored from callback response');
      }

      // Always record login timestamp so 7-day session window starts now
      localStorage.setItem('auth_login_time', Date.now().toString());

      // Store user display data in localStorage
      const email = data.email || data.userEmail || data.emailAddress;
      if (email) {
        localStorage.setItem('userEmail', email);
      } else {
        console.error('❌ No email to store');
      }

      const name = data.name || data.userName || data.displayName || data.given_name;
      if (name) {
        localStorage.setItem('userName', name);
      }

      const pictureUrl = data.pictureUrl || data.picture || data.avatar || data.profilePicture;
      if (pictureUrl) {
        localStorage.setItem('userPicture', pictureUrl);
      }

      const userId = data.userId || data.id || data.sub || data.user_id;
      if (userId) {
        localStorage.setItem('userId', userId.toString());
      }

      // Verify the data was actually stored
      if (!localStorage.getItem('userEmail')) {
        console.error('CRITICAL ERROR: Email was not stored properly!');
        setError('Authentication failed: Unable to save user data. Please try again.');
        setLoading(false);
        return;
      }
      
      // Mark cookie-based auth as active (backend sets HttpOnly cookie)
      setCookieAuthVerified(true);
      clearExplicitLogout();
      
      // Fire Google Ads conversion event for successful signup
      console.log("🔥 SIGNUP SUCCESS EVENT FIRED");
      
      window.dataLayer = window.dataLayer || [];
      
      setTimeout(() => {
        window.dataLayer.push({
          event: "signup_success"
        });
      }, 500);
      
      // Trigger custom events for same-window detection
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new Event('authChanged'));
      
      // Navigate to /home for Google Ads conversion tracking
      window.location.href = '/home';
    } catch (err) {
      console.error('Callback error:', err);
      setError('Failed to complete authentication: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setLoading(true);
    setError('');

    try {
      // console.log('=== STARTING GOOGLE OAUTH ===');
      // console.log('Calling AWS auth endpoint:', AUTH_API_URL);
      
      // IMPORTANT: Backend handles OAuth callback and redirects to frontend
      // We need to pass a frontend_redirect parameter so backend knows where to send the user after auth.
      
      // Determine the correct frontend URL based on current domain
      const currentOrigin = window.location.origin;
      const isLocalhost = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1');

      // Prefer the actual origin (dev/prod), fall back to localhost if unavailable
      const frontendBaseUrl = currentOrigin || BACKEND_URL;
      const frontendRedirectUrl = `${frontendBaseUrl}/signup`;
      
      // console.log('Frontend redirect URL:', frontendRedirectUrl);
      
      // Call the AWS API to get the Google OAuth redirect URL
      // Pass frontend_redirect so backend knows where to redirect after processing OAuth
      const response = await fetch(`${AUTH_API_URL}?frontend_redirect=${encodeURIComponent(frontendRedirectUrl)}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // console.log('AWS auth init response:', data);

      if (data.redirectUrl) {
      // console.log('Redirecting to Google OAuth page...');
      // console.log('Redirect URL:', data.redirectUrl);
        // Redirect user to Google OAuth page
        window.location.href = data.redirectUrl;
      } else {
        console.error('No redirectUrl in response:', data);
        setError('Failed to get Google sign-in URL');
        setLoading(false);
      }
    } catch (err) {
      console.error('Auth init error:', err);
      setError('Failed to initialize Google Sign-In. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="signup-container">
      <SEO 
        title="Sign Up - BlackKiteAI Interview Assistant"
        description="Create your free BlackKiteAI account and start your 7-day trial. Get AI-powered real-time assistance for your job interviews."
        canonical="https://www.blackkiteai.com/signup"
      />
      <div className="signup-card">
        <h2>Sign up or Login with Gmail</h2>
        <p>Continue with your Google account to access BlackKiteAI.</p>

        {isWebView && (
          <div className="webview-warning">
            <div className="webview-warning-icon">⚠️</div>
            <div className="webview-warning-text">
              <strong>Google Sign-In is not supported in {webViewAppName}'s browser.</strong>
              <p>For security, Google blocks sign-in from in-app browsers. Please open this page in Safari or Chrome instead.</p>
            </div>
            <button
              className="btn-open-browser"
              onClick={() => openInSystemBrowser()}
            >
              Open in Browser
            </button>
            <div className="webview-copy-url">
              <span>Or copy this link and paste in your browser:</span>
              <code onClick={() => {
                navigator.clipboard.writeText(window.location.origin + '/signup');
                alert('Link copied!');
              }}>
                {window.location.origin}/signup
              </code>
            </div>
          </div>
        )}

        {error && <div style={{ color: '#f87171', marginBottom: 12, padding: '10px', background: 'rgba(248, 113, 113, 0.1)', borderRadius: '8px' }}>{error}</div>}
        {loading && <div style={{ color: '#4facfe', marginBottom: 12 }}>Processing authentication...</div>}
        
        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.5rem 0' }}>
          <button 
            className="btn-google-signin" 
            onClick={handleGoogleSignIn}
            disabled={loading || isWebView}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: '12px' }}>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
        
        <div style={{ marginTop: 12 }}>
          <button className="btn-ghost" onClick={() => navigate('/')}>Back</button>
        </div>
      </div>
    </div>
  );
}

export default Signup;
